/**
 * Attendance grid (M17.9) at /operations/attendance. Lists attendance
 * records by date range with late / early-departure / overtime
 * highlighting.
 */

import { useQuery } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  EmptyState,
  OperationsReportsButton,
  PageHeader,
} from "../components/ui";
import { HrInitiateLink } from "../components/hr_operations";
import { LateDetectionPanel } from "../components/attendance/LateDetectionPanel";
import { api } from "../lib/api";
import type { AttendanceDetail } from "../lib/api";

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function fmtMinutes(m: number): string {
  if (m === 0) return "—";
  const hours = Math.floor(m / 60);
  const mins = m % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

export function AttendanceGridPage() {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState(daysAgoISO(14));
  const [dateTo, setDateTo] = useState(todayISO());

  const q = useQuery({
    queryKey: ["attendance", dateFrom, dateTo],
    queryFn: () => api.listAttendance({ dateFrom, dateTo }),
    enabled: !!dateFrom && !!dateTo,
  });

  const rows = useMemo<AttendanceDetail[]>(() => q.data ?? [], [q.data]);
  const lateCount = rows.filter((r) => r.late_minutes > 0).length;
  const otCount = rows.filter((r) => r.overtime_minutes > 0).length;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.attendance")}
        subtitle={
          rows.length > 0
            ? t("operations.att_summary", {
                rows: rows.length,
                late: lateCount,
                ot: otCount,
              })
            : undefined
        }
        breadcrumbs={[
          { label: t("nav.operations"), href: "/operations" },
          { label: t("nav.attendance") },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <a
              href="/operations/attendance/ot-calculator"
              className="rounded-md border border-brand-blue px-2 py-1 text-brand-blue hover:bg-brand-blue/5"
            >
              {t("operations.ot_calculator_link", "Article 107 OT calculator →")}
            </a>
            <HrInitiateLink
              transactionType="attendance"
              label={t("operations.hr_initiate_attendance")}
            />
            <HrInitiateLink
              transactionType="overtime"
              label={t("operations.hr_initiate_overtime")}
            />
            <OperationsReportsButton
              domain="attendance"
              domainLabel={t("nav.attendance", "Attendance")}
            />
            <label className="flex items-center gap-1">
              <span className="text-text-secondary">{t("operations.att_from")}</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded border border-border-default px-2 py-1"
              />
            </label>
            <label className="flex items-center gap-1">
              <span className="text-text-secondary">{t("operations.att_to")}</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded border border-border-default px-2 py-1"
              />
            </label>
          </div>
        }
      />

      <LateDetectionPanel />

      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorPanel message={(q.error as Error).message} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          headline={t("operations.attendance_no_records")}
          description={t("operations.att_no_records_body")}
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface-default">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle bg-surface-muted text-xs uppercase text-text-secondary">
              <tr>
                <th className="px-3 py-2 text-start">{t("operations.att_col_date")}</th>
                <th className="px-3 py-2 text-start">{t("operations.att_col_employee")}</th>
                <th className="px-3 py-2 text-start">{t("operations.att_col_in")}</th>
                <th className="px-3 py-2 text-start">{t("operations.att_col_out")}</th>
                <th className="px-3 py-2 text-start">{t("operations.att_col_late")}</th>
                <th className="px-3 py-2 text-start">{t("operations.att_col_early")}</th>
                <th className="px-3 py-2 text-start">{t("operations.att_col_overtime")}</th>
                <th className="px-3 py-2 text-start">{t("operations.att_col_source")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border-subtle last:border-0">
                  <td className="px-3 py-2 font-mono">{r.attendance_date}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.employee_id.slice(0, 8)}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.clock_in_time ? new Date(r.clock_in_time).toISOString().slice(11, 16) : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.clock_out_time ? new Date(r.clock_out_time).toISOString().slice(11, 16) : "—"}
                  </td>
                  <td
                    className={
                      r.late_minutes > 0
                        ? "px-3 py-2 font-medium text-amber-700"
                        : "px-3 py-2 text-text-tertiary"
                    }
                  >
                    {fmtMinutes(r.late_minutes)}
                  </td>
                  <td
                    className={
                      r.early_departure_minutes > 0
                        ? "px-3 py-2 font-medium text-amber-700"
                        : "px-3 py-2 text-text-tertiary"
                    }
                  >
                    {fmtMinutes(r.early_departure_minutes)}
                  </td>
                  <td
                    className={
                      r.overtime_minutes > 0
                        ? "px-3 py-2 font-medium text-emerald-700"
                        : "px-3 py-2 text-text-tertiary"
                    }
                  >
                    {fmtMinutes(r.overtime_minutes)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="rounded bg-surface-muted px-1.5 py-0.5 text-text-secondary">{r.source}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
